import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';

import { FlexBox } from '@eltoro-ui/components';
import logo from 'assets/Images/bw-avatar.svg';

import { AIRespondStatus, GenerationTypeEnum, IntentStatus } from 'enums';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/pro-light-svg-icons';
import {
  applyToPrompt,
  approvePrompt,
  makeChangePrompt,
} from 'Requests/Request_Methods/campaignMethods';
import { useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { AIGenerationResponseType } from 'types';
import { Button } from 'primereact/button';
import ZoomImageModal from 'Pages/CampaignCreation/components/GeneratedCampaign/GeneratedCampaignContent/EditWithAIModal/ZoomImageModal';

type AIAnswerProps = {
  index: number;
  status: AIRespondStatus | null;
  type: GenerationTypeEnum | null;
  onFocus: (prompt: AIGenerationResponseType | null, success: boolean) => void;
  onClose: VoidFunction;
  imgUrl?: string;
  userName: string;
  intent?: IntentStatus;
  approveLatest?: boolean;
  bannerId?: string;
  promptId?: string;
  parent_gen_banner_prompt_uuid: string | null;
  onUpdateCampaign: () => Promise<void>;
  conversationData: AIGenerationResponseType[];
  hasAIError?: boolean;
  setDisableSend: Dispatch<SetStateAction<boolean>>;
  goToBannerEditor?: VoidFunction;
  disableBtn: boolean;
};
const AIAnswer = ({
  index,
  status,
  type,
  onFocus,
  onClose,
  imgUrl,
  userName,
  intent,
  approveLatest,
  bannerId,
  promptId,
  parent_gen_banner_prompt_uuid,
  onUpdateCampaign,
  conversationData,
  hasAIError,
  setDisableSend,
  goToBannerEditor,
  disableBtn,
}: AIAnswerProps) => {
  const { campaignId } = useParams<{ campaignId: string }>();
  const [applyAllLaoding, setApplyAllLoading] = useState(false);
  const [applyOneLaoding, setApplyOneLoading] = useState(false);
  const [makeChangeLaoding, setMakeChangeLoading] = useState(false);
  const [clickedBanner, setClickedBanner] = useState(false);
  let messageText = '';
  if (type === GenerationTypeEnum.INITIAL) {
    messageText = `👋 Hi ${userName}! Here's the banner you'd like to edit. To get even better results, feel free to explore the Prompt Library below — it’s a great way to spark ideas and fine-tune your banner!`;
  } else if (!status) {
    messageText =
      'Oops, I’m sorry! 🥴 I can’t make more changes right now, but why not try our custom banner generator? ✨ It gives you full control to create exactly what you need — your banner, your rules! 🌟';
  } else if (status === AIRespondStatus.COMPLETED || status === AIRespondStatus.APPROVED) {
    messageText =
      'Here’s the banner you requested! ✨ Let me know if there’s anything else you’d like to tweak or add — I’m here to make it perfect!';
  } else if (
    status === AIRespondStatus.GENERATION_FAILED ||
    status === AIRespondStatus.CONVERSION_FAILED
  ) {
    if (intent === IntentStatus.IRRELEVANT || intent === IntentStatus.NON_ACTIONABLE_RELATED) {
      messageText =
        'Ads are my thing! Just so you know, my special skills are all about ads, think of me as your personal ad genie 🧞‍♂️. If it’s ad-related, I’m ready to work my magic!';
    } else {
      messageText =
        'Oops, looks like this one is a bit beyond my superpowers 🪄 But no worries — dive into the Prompt Library below for some inspiration and speedy results! ✨';
    }
  } else if (hasAIError) {
    messageText =
      'Oops, looks like this one is a bit beyond my superpowers 🪄 But no worries — dive into the Prompt Library below for some inspiration and speedy results! ✨';
  }

  useEffect(() => {
    if (applyAllLaoding || applyOneLaoding || makeChangeLaoding) {
      setDisableSend(true);
    } else {
      setDisableSend(false);
    }
  }, [applyAllLaoding, applyOneLaoding, makeChangeLaoding]);

  const approveOne = (campaignId: string, bannerId?: string, promptId?: string) => {
    if (campaignId && bannerId && promptId) {
      setApplyOneLoading(true);
      approvePrompt(campaignId, bannerId, promptId)
        .then(() => {
          onUpdateCampaign();
          onClose();
          toast.success('Banner successfully approved!');
        })
        .catch(err => {
          toast.error('Failed to approve changes, please try once more.');
        })
        .finally(() => {
          setApplyOneLoading(false);
        });
    }
  };
  const makeChange = (campaignId: string, bannerId?: string, promptId?: string) => {
    if (campaignId && bannerId && promptId) {
      if (
        promptId !== conversationData[conversationData.length - 1].parent_gen_banner_prompt_uuid
      ) {
        setMakeChangeLoading(true);
        makeChangePrompt(campaignId, bannerId, promptId)
          .then(res => {
            if (res.data) onFocus(res.data, true);
          })
          .catch(err => {
            toast.error('Failed to make changes, please try once more.');
          })
          .finally(() => {
            setMakeChangeLoading(false);
          });
      } else {
        onFocus(null, false);
      }
    }
  };

  const approveToAll = (campaignId: string, bannerId?: string, promptId?: string) => {
    if (campaignId && bannerId && promptId) {
      setApplyAllLoading(true);
      applyToPrompt(campaignId, bannerId, promptId)
        .then(() => {
          onUpdateCampaign();
          onClose();
          toast.success('Banner successfully approved!');
        })
        .catch(err => {
          toast.error('Failed to approve changes, please try once more.');
        })
        .finally(() => {
          setApplyAllLoading(false);
        });
    }
  };

  const getLatestCompletedBanner = (index: number) => {
    const sliced = conversationData.slice(0, index);
    const latest = sliced.reduceRight((acc: AIGenerationResponseType | null, item) => {
      return acc || (item.status === AIRespondStatus.COMPLETED ? item : null);
    }, null);
    if (latest) {
      approveOne(campaignId, latest.gen_banner_uuid, latest.gen_banner_prompt_uuid);
    }
  };
  const actions = !status ? (
    <FlexBox flexDirection="column" gap="8px" UNSAFE_className="action-wrapper">
      <button className="user-option" onClick={goToBannerEditor} disabled={disableBtn}>
        Go to Banner Generator
      </button>
      {!approveLatest && (
        <Button
          className="user-option"
          onClick={() => getLatestCompletedBanner(index)}
          disabled={disableBtn}
          loading={applyOneLaoding}
        >
          Approve latest changes
        </Button>
      )}
    </FlexBox>
  ) : type === GenerationTypeEnum.INITIAL &&
    conversationData.length > 1 &&
    conversationData.some(
      prompt =>
        (prompt.status === AIRespondStatus.COMPLETED &&
          prompt.generation_type !== GenerationTypeEnum.INITIAL) ||
        prompt.status === AIRespondStatus.APPROVED
    ) ? (
    <FlexBox flexDirection="column" gap="8px" UNSAFE_className="action-wrapper">
      <Button
        className="user-option"
        onClick={() => makeChange(campaignId, bannerId, promptId)}
        disabled={disableBtn}
        loading={makeChangeLaoding}
      >
        Make another change
      </Button>
    </FlexBox>
  ) : type !== GenerationTypeEnum.INITIAL && status === AIRespondStatus.COMPLETED ? (
    conversationData?.find(
      banner => banner.gen_banner_prompt_uuid === parent_gen_banner_prompt_uuid
    )?.status !== AIRespondStatus.APPROVED && (
      <FlexBox flexDirection="column" gap="8px" UNSAFE_className="action-wrapper">
        {parent_gen_banner_prompt_uuid !== conversationData[0].gen_banner_prompt_uuid && (
          <>
            <Button
              className="user-option"
              onClick={() => approveOne(campaignId, bannerId, promptId)}
              disabled={disableBtn}
              loading={applyOneLaoding}
            >
              Approve this version
            </Button>

            <Button
              className="user-option"
              onClick={() => approveToAll(campaignId, bannerId, promptId)}
              disabled={disableBtn}
              loading={applyAllLaoding}
            >
              Approve and apply to other banners if it fits
            </Button>
          </>
        )}

        {index !== conversationData.length - 1 && (
          <Button
            className="user-option"
            onClick={() => makeChange(campaignId, bannerId, promptId)}
            disabled={disableBtn}
            loading={makeChangeLaoding}
          >
            Make another change
          </Button>
        )}
      </FlexBox>
    )
  ) : type === GenerationTypeEnum.GENERATED && status === AIRespondStatus.APPROVED ? (
    <FlexBox flexDirection="column" UNSAFE_style={{ width: '100%' }}>
      <FlexBox gap="8px" UNSAFE_className="approved" alignItems="center" justifyContent="center">
        <FontAwesomeIcon icon={faCheck} color="#52C41A" />
        Approved
      </FlexBox>
      {index !== conversationData.length - 1 && (
        <FlexBox flexDirection="column" gap="8px" UNSAFE_className="action-wrapper">
          <Button
            className="user-option"
            onClick={() => makeChange(campaignId, bannerId, promptId)}
            disabled={disableBtn}
            loading={makeChangeLaoding}
          >
            Make another change
          </Button>
        </FlexBox>
      )}
    </FlexBox>
  ) : status === AIRespondStatus.CONVERSION_FAILED ||
    status === AIRespondStatus.GENERATION_FAILED ||
    hasAIError ? (
    <FlexBox flexDirection="column" gap="8px" UNSAFE_className="action-wrapper">
      {!approveLatest && (
        <Button
          className="user-option"
          onClick={() => getLatestCompletedBanner(index)}
          disabled={disableBtn}
          loading={applyOneLaoding}
        >
          Approve latest changes
        </Button>
      )}
    </FlexBox>
  ) : null;
  return (
    <FlexBox gap="8px" alignSelf="flex-start">
      <img src={logo} alt="logo" className="bw-logo" />
      <FlexBox
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        UNSAFE_className="msg-container"
        gap="12px"
      >
        <p className="message">{messageText}</p>
        {status &&
          status !== AIRespondStatus.GENERATION_FAILED &&
          status !== AIRespondStatus.CONVERSION_FAILED && (
            <button className="image-btn" onClick={() => setClickedBanner(true)}>
              <img src={imgUrl} alt="banner" className="banner-view" />
            </button>
          )}
        {actions}
      </FlexBox>
      {clickedBanner && imgUrl && (
        <ZoomImageModal bannerUrl={imgUrl} onClose={() => setClickedBanner(false)} />
      )}
    </FlexBox>
  );
};

export default AIAnswer;
